import React, { useMemo } from 'react'
import { AgreementTemplateFormField } from '../../swagger'
import DynamicFormField from '../Elements/DynamicFormField'
import { DynamicFieldStates } from '../Interfaces/DynamicFieldStates'
import { styled } from '@mui/material/styles'
import { toTitleCase } from '../../utils/stringUtility'

interface FormDataProps {
  formData?: AgreementTemplateFormField[]
  formDataStates: DynamicFieldStates
  updateFormDataValueForParent: (
    name: string,
    state: string,
    required: boolean,
    isOverMaxLength: boolean,
    isUnderMinLength: boolean
  ) => void
}

const FullWidthComponent = styled('section')({
  gridColumn: '1 / -1',
})

export const componentField = 'Component'
export const areaField = 'AreaName'

const containsAddress = (value: string) =>
  value.trim().toLowerCase().includes('address')

/** displayLabelMap entries based on keys returned by the endpoint */
const displayLabelMap: Record<string, string> = {
  Component: 'Component Name',
}
const displayLabel = (name: string): string => {
  if (displayLabelMap[name]) {
    return displayLabelMap[name]
  }
  const isPascalCaseWordPattern = /^[A-Z][a-z]+(?:[A-Z][a-z]+)+$/
  if (isPascalCaseWordPattern.test(name)) {
    return toTitleCase(name)
  }
  return name
}

export const FormData: React.FC<FormDataProps> = ({
  formData,
  formDataStates,
  updateFormDataValueForParent,
}) => {
  const formDataFields = useMemo(() => {
    if (!formData) return []

    const specialFields = formData.filter((field) => {
      const fieldName = field.inputName || field.displayName

      return (
        componentField === fieldName ||
        componentField.includes(fieldName) ||
        areaField === fieldName ||
        containsAddress(fieldName)
      )
    })
    const normalFields = formData.filter(
      (field) => !specialFields.includes(field)
    )

    const renderField = (
      data: AgreementTemplateFormField,
      isFullWidthField = true
    ) => {
      const name = data.displayName || data.inputName
      const field = (
        <DynamicFormField
          key={data.inputName}
          id={data.inputName}
          label={displayLabel(name)}
          name={name}
          dropdownOptions={data.dropdownSelectOptions?.map((option) => {
            return { id: option, name: option }
          })}
          updateForParent={updateFormDataValueForParent}
          value={formDataStates?.[name]?.state ?? ''}
          isValid={formDataStates?.[name]?.isValid}
          validationMessage={formDataStates?.[name]?.validationMessage}
          required={formDataStates?.[name]?.required}
          inputType={data.inputType}
          maxLength={data.maxTextLength !== -1 ? data.maxTextLength : undefined}
          minLength={data.minTextLength !== -1 ? data.minTextLength : undefined}
          fullWidth={isFullWidthField}
        />
      )
      return isFullWidthField ? (
        <FullWidthComponent key={field.key}>{field}</FullWidthComponent>
      ) : (
        field
      )
    }
    return [
      ...normalFields.map((normalField) => renderField(normalField, false)),
      ...specialFields?.map((field) => renderField(field)), // always at bottom, full width
    ]
  }, [formData, updateFormDataValueForParent, formDataStates])

  return <>{formDataFields}</>
}

export default FormData
